import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import NotificationPopup from '@/components/NotificationPopup';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Calendar, 
  UserCircle, 
  LogOut,
  Menu,
  X,
  Shield,
  CheckSquare,
  Package,
  LineChart,
  Target,
  Database,
  ShoppingCart,
  Receipt,
  MessageSquare,
  CreditCard,
  ChevronRight,
  Truck,
  Brain
} from 'lucide-react';

// Notification sound
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, 200);
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Request browser notification permission
const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

// Show browser notification
const showBrowserNotification = (title, body) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/logo192.png',
      vibrate: [200, 100, 200]
    });
  }
};

// New Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_empview360/artifacts/xp65b49y_yeni%20logo%20png.png";

export default function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingDeletionCount, setPendingDeletionCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const prevUnreadCount = useRef(0);
  const prevApprovalsCount = useRef(0);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      const newCount = res.data.count || 0;
      
      if (newCount > prevUnreadCount.current && prevUnreadCount.current !== 0) {
        playNotificationSound();
        showBrowserNotification('Yeni Bildirim', 'Yeni bir mesaj veya hatırlatma var!');
      }
      
      prevUnreadCount.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      console.log('Error fetching unread count');
    }
  };

  const fetchPendingDeletionCount = async () => {
    try {
      const res = await axios.get(`${API}/deletion-requests/pending-count`);
      setPendingDeletionCount(res.data.count || 0);
    } catch (error) {
      console.log('Error fetching deletion count:', error);
    }
  };

  const fetchPendingApprovalsCount = async () => {
    try {
      let totalPending = 0;
      
      const paymentsRes = await axios.get(`${API}/payments/pending-approvals`).catch(() => ({ data: [] }));
      totalPending += (paymentsRes.data?.length || 0);
      
      const leavesRes = await axios.get(`${API}/leave-requests/pending`).catch(() => ({ data: [] }));
      totalPending += (leavesRes.data?.length || 0);
      
      if (totalPending > prevApprovalsCount.current && prevApprovalsCount.current !== 0) {
        playNotificationSound();
        showBrowserNotification('Yeni Onay Talebi', `${totalPending} adet onay bekleyen talep var`);
      }
      
      prevApprovalsCount.current = totalPending;
      setPendingApprovalsCount(totalPending);
    } catch (error) {
      console.log('Error fetching pending approvals:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchPendingDeletionCount();
      fetchPendingApprovalsCount();
      const interval = setInterval(fetchPendingDeletionCount, 30000);
      const interval2 = setInterval(fetchPendingApprovalsCount, 10000);
      return () => {
        clearInterval(interval);
        clearInterval(interval2);
      };
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 5000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Main menu items (without Messages)
  const menuItems = [
    { path: '/', label: 'Genel Bakış', icon: LayoutDashboard },
    { path: '/requests', label: 'Talepler', icon: FileText },
    { path: '/sales', label: 'Satışlar', icon: ShoppingCart },
    { path: '/collections', label: 'Tahsilatlar', icon: Receipt },
    { path: '/customers', label: 'Müşteriler', icon: Users },
    { path: '/suppliers', label: 'Tedarikçiler', icon: Truck },
    { path: '/stock', label: 'Stok', icon: Package },
    { path: '/tasks', label: 'Görevler', icon: CheckSquare },
    { path: '/payments', label: 'Ödeme & İzin', icon: CreditCard, badge: pendingApprovalsCount },
    { path: '/calendar', label: 'Takvim', icon: Calendar },
    { path: '/goals', label: 'Hedefler', icon: Target },
    { path: '/team', label: 'Ekip', icon: UserCircle },
  ];

  // Add admin-only menu items if user is admin
  const allMenuItems = user?.role === 'admin' 
    ? [
        ...menuItems,
        { path: '/email-ai', label: 'E-posta AI', icon: Brain },
        { path: '/financial', label: 'Mali Durum', icon: LineChart },
        { path: '/backups', label: 'Yedekleme', icon: Database },
        { path: '/admin', label: 'Admin', icon: Shield, badge: pendingDeletionCount }
      ]
    : menuItems;

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isMessagesActive = location.pathname.startsWith('/messages');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col overflow-y-auto bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 shadow-2xl">
          
          {/* Logo Section */}
          <div className="flex h-20 shrink-0 items-center justify-center px-6 border-b border-slate-700/50">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Maris Logo" 
                className="h-12 w-auto drop-shadow-lg"
              />
              <div className="hidden xl:block">
                <h1 className="text-xl font-bold text-white tracking-tight">
                  Maris CRM
                </h1>
                <p className="text-xs text-slate-400">Takip Sistemi</p>
              </div>
            </div>
          </div>

          {/* Messages - Special Top Section */}
          <div className="px-4 pt-5 pb-3">
            <button
              onClick={() => navigate('/messages')}
              data-testid="nav-mesajlar"
              className={`w-full group relative overflow-hidden rounded-xl p-4 transition-all duration-300 ${
                isMessagesActive 
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 shadow-lg shadow-cyan-500/30' 
                  : 'bg-slate-700/50 hover:bg-slate-700 hover:shadow-lg'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isMessagesActive ? 'bg-white/20' : 'bg-slate-600'}`}>
                    <MessageSquare className={`w-5 h-5 ${isMessagesActive ? 'text-white' : 'text-cyan-400'}`} />
                  </div>
                  <div className="text-left">
                    <p className={`font-semibold ${isMessagesActive ? 'text-white' : 'text-slate-200'}`}>
                      Mesajlar
                    </p>
                    <p className={`text-xs ${isMessagesActive ? 'text-white/70' : 'text-slate-400'}`}>
                      Ekip iletişimi
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <span className={`flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-bold rounded-full animate-pulse ${
                      isMessagesActive ? 'bg-white text-cyan-600' : 'bg-cyan-500 text-white'
                    }`}>
                      {unreadCount}
                    </span>
                  )}
                  <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${
                    isMessagesActive ? 'text-white' : 'text-slate-400'
                  }`} />
                </div>
              </div>
              {/* Animated background effect */}
              {!isMessagesActive && (
                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-teal-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="px-6 py-2">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-slate-700/50" />
              <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Menü</span>
              <div className="h-px flex-1 bg-slate-700/50" />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 pb-4 overflow-y-auto">
            <ul className="space-y-1">
              {allMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <button
                      data-testid={`nav-${item.label.toLowerCase().replace(/ & /g, '-')}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 group ${
                        active
                          ? 'bg-gradient-to-r from-cyan-500/20 to-teal-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                      }`}
                      onClick={() => {
                        navigate(item.path);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <Icon className={`w-5 h-5 transition-colors ${
                        active ? 'text-cyan-400' : 'text-slate-400 group-hover:text-cyan-400'
                      }`} />
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="flex items-center justify-center min-w-[22px] h-5 px-1.5 text-xs font-bold rounded-full bg-orange-500 text-white">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-700/50">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 mb-3">
              <Avatar className="w-10 h-10 ring-2 ring-cyan-500/30">
                {user?.profile_image ? (
                  <AvatarImage src={user.profile_image} alt={user.full_name} />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white font-semibold">
                  {user?.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.full_name || 'Kullanıcı'}</p>
                <p className="text-xs text-slate-400 truncate">@{user?.username || 'user'}</p>
              </div>
              {user?.role === 'admin' && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                  Admin
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              data-testid="logout-button"
              className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Çıkış Yap
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-700">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Maris Logo" 
              className="h-9 w-auto"
            />
            <h1 className="text-lg font-bold text-white">
              Maris CRM
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Messages quick access on mobile */}
            <button
              onClick={() => navigate('/messages')}
              className="relative p-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-cyan-500 text-white animate-pulse">
                  {unreadCount}
                </span>
              )}
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              className="text-white hover:bg-slate-800"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-slate-900 pt-16 overflow-y-auto">
          {/* Messages Special Card - Mobile */}
          <div className="px-4 pt-4 pb-2">
            <button
              onClick={() => {
                navigate('/messages');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                isMessagesActive 
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500' 
                  : 'bg-slate-800'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isMessagesActive ? 'bg-white/20' : 'bg-slate-700'}`}>
                  <MessageSquare className={`w-5 h-5 ${isMessagesActive ? 'text-white' : 'text-cyan-400'}`} />
                </div>
                <span className={`font-semibold ${isMessagesActive ? 'text-white' : 'text-slate-200'}`}>
                  Mesajlar
                </span>
              </div>
              {unreadCount > 0 && (
                <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                  isMessagesActive ? 'bg-white text-cyan-600' : 'bg-cyan-500 text-white'
                }`}>
                  {unreadCount}
                </span>
              )}
            </button>
          </div>

          <nav className="px-4 py-4">
            <ul className="space-y-1">
              {allMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <button
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                        active
                          ? 'bg-gradient-to-r from-cyan-500/20 to-teal-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800'
                      }`}
                      onClick={() => {
                        navigate(item.path);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <Icon className={`w-5 h-5 ${active ? 'text-cyan-400' : 'text-slate-400'}`} />
                      <span className="flex-1">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-orange-500 text-white">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            
            <div className="mt-6 pt-6 border-t border-slate-700">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800 mb-4">
                <Avatar className="w-10 h-10 ring-2 ring-cyan-500/30">
                  {user?.profile_image ? (
                    <AvatarImage src={user.profile_image} alt={user.full_name} />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white font-semibold">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-semibold text-white">{user?.full_name || 'Kullanıcı'}</p>
                  <p className="text-xs text-slate-400">@{user?.username || 'user'}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5 mr-3" />
                Çıkış Yap
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:pl-72 pt-16 lg:pt-0">
        <div className="px-4 py-8 sm:px-6 lg:px-8">{children}</div>
      </main>

      {/* Global Notification Popup */}
      <NotificationPopup />
    </div>
  );
}
